import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import { compareWidgetTours, isWidgetTourEligible } from '../../helpers'
import { WidgetTourConfig } from '../../models'
import { WidgetTourContext } from '../../shared/WidgetTourContext'
import { markWidgetTourSeen } from '../../shared/widgetTourStorage'
import { WidgetTourRenderer } from '../widgetTourRenderer/WidgetTourRenderer'

export const WidgetTourProvider = ({ children }: PropsWithChildren) => {
  const [registeredTours, setRegisteredTours] = useState<Record<string, WidgetTourConfig>>({})
  const [tourQueue, setTourQueue] = useState<WidgetTourConfig[]>([])

  const activeTour = tourQueue[0] ?? null

  useEffect(() => {
    if (activeTour) {
      markWidgetTourSeen(activeTour.id)
    }
  }, [activeTour])

  const registerTour = useCallback((tour: WidgetTourConfig) => {
    setRegisteredTours((currentTours) => ({
      ...currentTours,
      [tour.id]: tour,
    }))
    setTourQueue((currentQueue) =>
      currentQueue.map((queuedTour, index) =>
        index > 0 && queuedTour.id === tour.id ? tour : queuedTour
      )
    )
  }, [])

  const unregisterTour = useCallback((tourId: string) => {
    setRegisteredTours((currentTours) => {
      if (!currentTours[tourId]) {
        return currentTours
      }

      const nextTours = { ...currentTours }
      delete nextTours[tourId]
      return nextTours
    })
    setTourQueue((currentQueue) => currentQueue.filter((tour) => tour.id !== tourId))
  }, [])

  const startTours = useCallback(
    (tourIds: string[], options?: { includeSeen?: boolean }) => {
      const requestedTours = [...new Set(tourIds)]
        .map((tourId) => registeredTours[tourId])
        .filter((tour): tour is WidgetTourConfig => Boolean(tour))
        .filter((tour) => isWidgetTourEligible(tour, options?.includeSeen))

      if (!requestedTours.length) {
        return []
      }

      setTourQueue((currentQueue) => {
        const active = currentQueue[0]
        const queuedIds = new Set(currentQueue.map((tour) => tour.id))
        const newTours = requestedTours.filter((tour) => !queuedIds.has(tour.id))
        const waitingTours = [...currentQueue.slice(active ? 1 : 0), ...newTours].sort(
          compareWidgetTours
        )

        return active ? [active, ...waitingTours] : waitingTours
      })

      return requestedTours.map((tour) => tour.id)
    },
    [registeredTours]
  )

  const completeActiveTour = useCallback(() => {
    setTourQueue((currentQueue) => currentQueue.slice(1))
  }, [])

  const dismissActiveTour = useCallback(() => {
    setTourQueue([])
  }, [])

  const contextValue = useMemo(
    () => ({
      activeTour,
      registerTour,
      unregisterTour,
      startTours,
    }),
    [activeTour, registerTour, startTours, unregisterTour]
  )

  return (
    <WidgetTourContext.Provider value={contextValue}>
      {children}
      {activeTour && (
        <WidgetTourRenderer
          key={activeTour.id}
          config={activeTour}
          onComplete={completeActiveTour}
          onDismiss={dismissActiveTour}
        />
      )}
    </WidgetTourContext.Provider>
  )
}
